import React, {
  useEffect, useCallback, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';

const CM_APP_ID_WEB = '42b5ea94-46e0-4f70-afd4-6f48b37327d6';
const custHeader = {
  Accept: 'application/json',
  'Content-Type': 'application/x-www-form-urlencoded',
  Authorization:
    `Basic ${
      window.btoa(
        `${
          CM_APP_ID_WEB
        }`
        + ':'
        + '',
      )}`,
};

const ConcurrencyHandler = ({
  selectedProviderId,
  hbaStatus,
  upstreamUserId,
  authenticated,
  authorized,
  fetchMetadata,
  conflictHandler,
}) => {
  const [sessionId, setSessionId] = useState('');
  const refreshTimer = useRef(null);
  const nextHeartbeatTime = useRef(0);

  const handleConflict = useCallback(() => {
    setSessionId('');
    conflictHandler();
  }, [conflictHandler, setSessionId]);

  const constructFormBody = useCallback(() => {
    const formBody = [];
    const encodedKey = encodeURIComponent('hba');
    const encodedValue = encodeURIComponent(hbaStatus);
    formBody.push(`${encodedKey}=${encodedValue}`);
    return formBody.join('&');
  }, [hbaStatus]);

  const initSession = useCallback(async (upsUserId) => {
    try {
      const url = `https://streams.adobeprimetime.com/v2/sessions/Spectrum/${upsUserId}`;
      const formBody = constructFormBody();
      const response = await fetch(url, {
        method: 'POST',
        headers: custHeader,
        body: formBody,
      });

      if (response.ok) {
        const location = response.headers.get('Location');
        if (location) {
          setSessionId(location);
        } else {
          console.error('Failed to get session ID from response');
        }
      } else {
        if (409 === response.status) {
          handleConflict();
        }
        console.error(
          'Failed to initialize session:',
          response.status,
          response.statusText,
        );
      }
    } catch (error) {
      console.error('Failed to initialize session:', error);
    }
  }, [handleConflict, constructFormBody]);

  const sendHeartbeat = useCallback(async (session, upsUserId) => {
    try {
      const url = `https://streams.adobeprimetime.com/v2/sessions/Spectrum/${upsUserId}/${session}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: custHeader,
      });

      if (response.ok) {
        const expires = response.headers.get('Expires');
        if (expires) {
          const currentTime = new Date().getTime();
          const expiresTime = new Date(expires).getTime();
          nextHeartbeatTime.current = expiresTime - currentTime;
          refreshTimer.current = setTimeout(() => {
            sendHeartbeat(session, upsUserId);
          }, nextHeartbeatTime.current);
        } else {
          console.error('Failed to get expires time from response');
        }
      } else {
        if (409 === response.status) {
          handleConflict();
        }
        if (410 === response.status) {
          setSessionId('');
          initSession(upsUserId);
        }
        console.error(
          'Failed to send heartbeat:',
          response.status,
          response.statusText,
        );
      }
    } catch (error) {
      console.error('Failed to send heartbeat:', error);
    }
  }, [handleConflict]);

  useEffect(() => {
    if ((authorized && authenticated) && 'Spectrum' === selectedProviderId) {
      fetchMetadata();
      if ('' !== upstreamUserId && '' !== hbaStatus) {
        initSession(upstreamUserId);
      }
    }
  }, [
    authorized,
    upstreamUserId, hbaStatus, authenticated, selectedProviderId]);

  useEffect(() => {
    if ((authorized && authenticated) && 'Spectrum' === selectedProviderId) {
      if ('' !== upstreamUserId && '' !== sessionId) {
        sendHeartbeat(sessionId, upstreamUserId);
      }
    }
    return () => {
      clearInterval(refreshTimer.current);
      refreshTimer.current = null;
    };
  }, [
    authorized, authenticated,
    upstreamUserId, sessionId, selectedProviderId]);

  return (
    <div id="concurency-handler" />
  );
};

ConcurrencyHandler.defaultProps = {
  selectedProviderId: '',
  hbaStatus: '',
  upstreamUserId: '',
  authenticated: false,
  authorized: false,
  fetchMetadata: () => {},
  conflictHandler: () => {},
};

ConcurrencyHandler.propTypes = {
  selectedProviderId: PropTypes.string,
  hbaStatus: PropTypes.string,
  upstreamUserId: PropTypes.string,
  authenticated: PropTypes.bool,
  authorized: PropTypes.bool,
  fetchMetadata: PropTypes.func,
  conflictHandler: PropTypes.func,
};

export default ConcurrencyHandler;
